import React, { useEffect, useState, useRef } from "react";
import { BrowserRouter as WithOutHash } from "react-router-dom";
import Routes from "../routes";
import { ModuleContextProvider } from "../context/Context";
import "../App.css";
import TutorialManager from "../modules/Tutorial/TutorialManager";
import useHydrateFilterState from "../utils/hooks/useHydrateFilterState";
import { useDispatch } from "react-redux";
import { fetchGeoStates } from "../Redux/geo/geoThunk";
import { useIdleTimer } from "../Common/useIdleTimer";
import { Modal, Button } from "antd";
import { endSession } from "../Redux/sessionSlice";
import myCommonApiCall from "../Common/apicalls";
import secureLocalStorage from "react-secure-storage";

function App() {
  useHydrateFilterState();
  const dispatch = useDispatch();

  const [showWarning, setShowWarning] = useState(false);
  const [showSessionEnded, setShowSessionEnded] = useState(false);

  const trackingPausedRef = useRef(false);
  const resetIdleTimersRef = useRef(null);

  const clearAndRestartApp = () => {
    setShowWarning(false);
    setShowSessionEnded(false);
    trackingPausedRef.current = false;

    myCommonApiCall.clearLocalSession();
    secureLocalStorage.removeItem("filter-selections");
    sessionStorage.clear();

    dispatch(endSession());

    window.location.href = "/";
  };

  const handleWarning = () => {
    trackingPausedRef.current = true;
    setShowSessionEnded(false);
    setShowWarning(true);
  };

  const handleTimeout = async () => {
    trackingPausedRef.current = true;
    setShowWarning(false);

    const token = secureLocalStorage.getItem("token");

    if (token) {
      try {
        await myCommonApiCall.signOut();
      } catch (error) {
        console.error("Sign out request failed:", error);
      }
    }

    myCommonApiCall.clearLocalSession();
    dispatch(endSession());
    setShowSessionEnded(true);
  };

  const handleContinue = async () => {
    const token = secureLocalStorage.getItem("token");

    if (!token) {
      setShowWarning(false);
      setShowSessionEnded(false);
      trackingPausedRef.current = false;

      if (resetIdleTimersRef.current) {
        resetIdleTimersRef.current();
      }

      return;
    }

    try {
      const result = await myCommonApiCall.refreshToken();

      if (!result?.ok) {
        throw new Error("Token refresh failed");
      }

      setShowWarning(false);
      setShowSessionEnded(false);
      trackingPausedRef.current = false;

      if (resetIdleTimersRef.current) {
        resetIdleTimersRef.current();
      }
    } catch (error) {
      console.error("Failed to refresh token:", error);

      try {
        await myCommonApiCall.signOut();
      } catch (signOutError) {
        console.error("Sign out after refresh failure failed:", signOutError);
      }

      myCommonApiCall.clearLocalSession();
      dispatch(endSession());
      setShowWarning(false);
      setShowSessionEnded(true);
      trackingPausedRef.current = true;
    }
  };

  const { warningSecondsRemaining } = useIdleTimer(
    handleTimeout,
    handleWarning,
    {
      trackingPausedRef,
      onResetRef: resetIdleTimersRef,
    },
  );

  useEffect(() => {
    dispatch(fetchGeoStates());
  }, [dispatch]);

  const minutes = Math.floor(warningSecondsRemaining / 60);
  const seconds = warningSecondsRemaining % 60;
  const formattedCountdown = `${minutes}:${String(seconds).padStart(2, "0")}`;

  return (
    <WithOutHash>
      <ModuleContextProvider>
        <Routes />
        <TutorialManager />

        <canvas
          id="confetti-container"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 10005,
          }}
        />

        <Modal
          width="500px"
          open={showWarning}
          closable={true}
          maskClosable={true}
          keyboard={true}
          footer={null}
          onCancel={handleContinue}
        >
          <div style={{ paddingTop: 8 }}>
            <h3 style={{ marginBottom: 12 }}>Inactivity Timeout</h3>
            <p style={{ marginBottom: 8 }}>
              This page will reset due to inactivity.
            </p>
            <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
              Time remaining: {formattedCountdown}
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 12,
              }}
            >
              <Button onClick={handleTimeout}>Logout</Button>
              <Button type="primary" onClick={handleContinue}>
                Continue
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          width="500px"
          open={showSessionEnded}
          closable={true}
          maskClosable={true}
          keyboard={true}
          cancelButtonProps={{ style: { display: "none" } }}
          okText="OK"
          onOk={clearAndRestartApp}
          onCancel={clearAndRestartApp}
        >
          <div style={{ paddingTop: 8 }}>
            <h3 style={{ marginBottom: 12 }}>Session Ended</h3>
            <p>Your session has ended and the app will restart.</p>
          </div>
        </Modal>
      </ModuleContextProvider>
    </WithOutHash>
  );
}

export default App;
